import { useState, useMemo, useCallback } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Divider,
  FormControl,
  FormLabel,
  Modal,
  TextField,
  Autocomplete,
  FormHelperText,
  Select,
  MenuItem,
} from "@mui/material";
import { Form, Formik } from "formik";
import * as yup from "yup";
import { errorToastMessage, toastMessage } from "../../utils/toast";
import {
  ModalDrawerButtonStyles,
  ModalDrawerStyles,
  ModalHeader,
} from "../Common/styles/modal";
import { InputWrapper, LabelStyle } from "../Common/styles/form";
import { DrawerForm } from "../Common/styles/drawer";
import http from "../../utils/http";
import { debounce } from "lodash";
import { AxiosResponse } from "axios";
import { DatePicker, TimePicker } from "@mui/x-date-pickers";
import { DateTime } from "luxon";
import { Slot } from "../../Redux/reducers/appointmentSlice";

const schema = yup.object().shape({
  slotId: yup.string(),
  date: yup.string().when("slotId", {
    is: (val: string) => !val || val === "",
    then: (schema) => schema.required("Date is required"),
    otherwise: (schema) => schema.optional(),
  }),
  startTime: yup
    .string()
    .when("slotId", {
      is: (val: string) => !val || val === "",
      then: (schema) => schema.required("Start time is required"),
      otherwise: (schema) => schema.optional(),
    })
    .test("is-valid-time", "Start time must be valid", function (value) {
      if (!value) return true;
      const { slotId } = this.parent;
      if (slotId) return true;
      try {
        DateTime.fromFormat(value, "HH:mm");
        return true;
      } catch {
        return false;
      }
    }),
  endTime: yup
    .string()
    .when("slotId", {
      is: (val: string) => !val || val === "",
      then: (schema) => schema.required("End time is required"),
      otherwise: (schema) => schema.optional(),
    })
    .test(
      "is-after-start",
      "End time must be after start time",
      function (value) {
        const { startTime, slotId } = this.parent;
        if (!value || !startTime || slotId) return true;
        try {
          const start = DateTime.fromFormat(startTime, "HH:mm");
          const end = DateTime.fromFormat(value, "HH:mm");
          const diffInMinutes: any = end
            .diff(start, "minutes")
            .toObject().minutes;
          return diffInMinutes > 0;
        } catch {
          return false;
        }
      }
    ),
  studyId: yup.string().required("Study is required"),
  participantIds: yup
    .array()
    .of(yup.string())
    .min(1, "At least one participant is required")
    .required("Participants are required"),
  inviterIds: yup.array().of(yup.string()).optional(),
  notes: yup.string().optional(),
});

type Props = {
  showModal: boolean;
  closeModal: () => void;
  slotData: Slot | null;
  roomId: string;
  refreshPage: () => void;
};

const AddAppointmentModal = ({
  showModal,
  closeModal,
  slotData,
  roomId,
  refreshPage,
}: Props) => {
  const [submitLoader, setSubmitLoader] = useState(false);
  const [studies, setStudies] = useState<any[]>([]);
  const [studiesLoading, setStudiesLoading] = useState(false);
  const [cohorts, setCohorts] = useState<any[]>([]);
  const [cohortLoading, setCohortLoading] = useState(false);
  const [participants, setParticipants] = useState<any[]>([]);
  const [participantsLoading, setParticipantsLoading] = useState(false);
  const [inviters, setInviters] = useState<any[]>([]);
  const [invitersLoading, setInvitersLoading] = useState(false);

  // Handle study search with debounce
  const handleStudySearch = useMemo(
    () =>
      debounce(async (value: string) => {
        if (value.length <= 2) {
          return;
        }
        try {
          setStudiesLoading(true);

          let url = `/platform/study`;
          if (value) {
            url += `?query=${value}`;
          }
          const res: AxiosResponse = await http.get(url);
          const studyList = res.data?.data?.map((study: any) => {
            return {
              id: study?.id,
              name: study?.name,
            };
          });
          setStudies(studyList);
        } catch (err) {
          errorToastMessage(err as Error);
        } finally {
          setStudiesLoading(false);
        }
      }, 500),
    []
  );

  // useEffect(() => {
  //   handleStudySearch("");
  // }, [handleStudySearch]);

  // Handle participant search with debounce
  const handleParticipantSearch = useCallback(
    async (studyId: string, cohortId: string) => {
      try {
        setParticipantsLoading(true);
        let url = `/admin/registry/participants/minimal?studyId=${studyId}&cohortId=${cohortId}`;
        const res: AxiosResponse = await http.get(url);
        const participantList = res.data?.data?.map((participant: any) => {
          const fullName = participant?.firstName
            ? `${participant.firstName} ${participant?.lastName || ""}`.trim()
            : "";
          const idStr = participant?.groupCode || participant?.subjectId || "";

          let displayName = idStr;
          if (fullName) {
            displayName = idStr ? `${idStr}: ${fullName}` : fullName;
          }

          return {
            id: participant?.id,
            name: displayName || "Name Unknown",
          };
        });
        setParticipants(participantList);
      } catch (err) {
        errorToastMessage(err as Error);
      } finally {
        setParticipantsLoading(false);
      }
    },
    [setParticipantsLoading]
  );

  // Handle inviter search with debounce
  const handleInviterSearch = useCallback(
    async (studyId: string) => {
      try {
        setInvitersLoading(true);
        let url = `/platform/users/search?studyId=${studyId}`;
        // if (value) {
        //   url += `?search=${value}`;
        // }
        const res: AxiosResponse = await http.get(url);
        const inviterList = res.data?.data?.map((inviter: any) => {
           const fullName = inviter?.firstName
            ? `${inviter?.firstName} ${inviter?.lastName || ""}`.trim()
            : "";
          return {
            id: inviter?.id,
            name:fullName || inviter?.email || "Name Unknown",
          };
        });
        setInviters(inviterList);
      } catch (err) {
        errorToastMessage(err as Error);
      } finally {
        setInvitersLoading(false);
      }
    },
    [setInvitersLoading]
  );

  const handleCohorts = useCallback(async (studyId: string) => {
    try {
      setCohortLoading(true);
      let url = `/admin/registry/participant-groups?studyId=${studyId}`;
      const res: AxiosResponse = await http.get(url);
      const cohortList = res.data?.data?.map((cohort: any) => {
        return {
          id: cohort?.id,
          name: cohort.name,
        };
      });
      setCohorts(cohortList);
    } catch (err) {
      errorToastMessage(err as Error);
    } finally {
      setCohortLoading(false);
    }
  }, []);

  const submitHandler = async (values: any) => {
    try {
      setSubmitLoader(true);
      const payload: any = {
        studyId: values.studyId,
        invitees: values.participantIds,
        roomId: roomId,
        inviters: values.inviterIds,
        notes: values.notes,
        status: "scheduled",
      };
      let url;
      if (slotData?.id) {
        url = `/admin/registry/appointment-rooms/${roomId}/slots/${slotData?.id}/book`;
      } else {
        url = `/admin/registry/appointment-rooms/${roomId}/slots/quick-book`;
        payload.date = DateTime.fromFormat(values.date, "MM/dd/yyyy").toFormat(
          "yyyy-MM-dd"
        );
        payload.startTime = values.startTime;
        payload.endTime = values.endTime;
      }
      await http.post(url, payload);
      toastMessage("success", "Appointment created successfully");
      refreshPage();
      closeModal();
    } catch (err) {
      errorToastMessage(err as Error);
    } finally {
      setSubmitLoader(false);
    }
  };

  return (
    <Modal open={showModal} onClose={closeModal}>
      <Box sx={ModalDrawerStyles}>
        <ModalHeader
          title={
            slotData?.id ? "Create Appointment" : "Create Manual Appointment"
          }
          onCloseClick={closeModal}
        />

        <Formik
          initialValues={{
            studyId: "",
            participantIds: [],
            inviterIds: [],
            notes: "",
            slotId: slotData?.id || "",
            date: DateTime.now().toFormat("MM/dd/yyyy"),
            startTime: "09:00",
            endTime: "10:00",
            cohortId: "",
          }}
          validationSchema={schema}
          onSubmit={(values) => {
            submitHandler(values);
          }}
        >
          {({ getFieldProps, errors, touched, setFieldValue, values }) => (
            <Form style={DrawerForm}>
              <Box sx={{ flex: 1 }}>
                {/* Manual Appointment Date & Time Fields - Only show when no slot is selected */}
                {!values.slotId && (
                  <>
                    {/* Date Picker */}
                    <FormControl sx={InputWrapper}>
                      <FormLabel sx={LabelStyle}>
                        Date <span style={{ color: "red" }}>*</span>
                      </FormLabel>
                      <DatePicker
                        disablePast
                        format="MM/dd/yyyy"
                        value={
                          values.date
                            ? DateTime.fromFormat(values.date, "MM/dd/yyyy")
                            : null
                        }
                        onChange={(newValue: any) => {
                          if (newValue) {
                            setFieldValue(
                              "date",
                              newValue.toFormat("MM/dd/yyyy")
                            );
                          }
                        }}
                        slotProps={{
                          textField: {
                            inputProps: {
                              readOnly: true,
                              placeholder: "Select date",
                              id: "date",
                            },
                            error: touched.date && Boolean(errors.date),
                            helperText:
                              touched.date && errors.date
                                ? (errors.date as string)
                                : " ",
                          },
                        }}
                      />
                    </FormControl>

                    {/* Time Range */}
                    <Box sx={{ display: "flex", gap: 2 }}>
                      <FormControl sx={InputWrapper}>
                        <FormLabel sx={LabelStyle}>
                          Start Time <span style={{ color: "red" }}>*</span>
                        </FormLabel>
                        <TimePicker
                          ampm={false}
                          format="hh:mm a"
                          value={
                            values.startTime
                              ? DateTime.fromFormat(values.startTime, "HH:mm")
                              : null
                          }
                          onChange={(newValue: any) => {
                            if (newValue) {
                              setFieldValue(
                                "startTime",
                                newValue.toFormat("HH:mm")
                              );
                            }
                          }}
                          slotProps={{
                            textField: {
                              inputProps: {
                                placeholder: "Select start time",
                                id: "startTime",
                              },
                            },
                          }}
                          sx={{
                            border:
                              touched.startTime && errors.startTime
                                ? "1px solid red"
                                : undefined,
                            borderRadius:
                              touched.startTime && errors.startTime
                                ? "8px"
                                : undefined,
                          }}
                        />
                        <FormHelperText
                          error={Boolean(touched.startTime && errors.startTime)}
                        >
                          {touched.startTime && errors.startTime
                            ? (errors.startTime as string)
                            : " "}
                        </FormHelperText>
                      </FormControl>

                      <FormControl sx={InputWrapper}>
                        <FormLabel sx={LabelStyle}>
                          End Time <span style={{ color: "red" }}>*</span>
                        </FormLabel>
                        <TimePicker
                          ampm={false}
                          format="hh:mm a"
                          value={
                            values.endTime
                              ? DateTime.fromFormat(values.endTime, "HH:mm")
                              : null
                          }
                          onChange={(newValue: any) => {
                            if (newValue) {
                              setFieldValue(
                                "endTime",
                                newValue.toFormat("HH:mm")
                              );
                            }
                          }}
                          minTime={
                            values.startTime
                              ? DateTime.fromFormat(
                                  values.startTime,
                                  "HH:mm"
                                ).plus({ minutes: 1 })
                              : undefined
                          }
                          slotProps={{
                            textField: {
                              inputProps: {
                                placeholder: "Select end time",
                                id: "endTime",
                              },
                            },
                          }}
                          sx={{
                            border:
                              touched.endTime && errors.endTime
                                ? "1px solid red"
                                : undefined,
                            borderRadius:
                              touched.endTime && errors.endTime
                                ? "8px"
                                : undefined,
                          }}
                        />
                        <FormHelperText
                          error={Boolean(touched.endTime && errors.endTime)}
                        >
                          {touched.endTime && errors.endTime
                            ? (errors.endTime as string)
                            : " "}
                        </FormHelperText>
                      </FormControl>
                    </Box>
                  </>
                )}

                {/* Study Selection */}
                <FormControl sx={InputWrapper}>
                  <FormLabel sx={LabelStyle}>
                    Study <span style={{ color: "red" }}>*</span>
                  </FormLabel>
                  <Autocomplete
                    filterOptions={(x) => x}
                    onInputChange={(_1: any, value: any, reason: string) => {
                      if (reason === "input") handleStudySearch(value);
                    }}
                    onChange={(_1: any, value: any) => {
                      setFieldValue("studyId", value?.id || "");
                      if (value?.id) {
                        handleCohorts(value?.id);
                        handleInviterSearch(value?.id);
                      }
                      setCohorts([]);
                      setParticipants([]);
                      setInviters([]);
                      setFieldValue("participantIds", []);
                      setFieldValue("inviterIds", []);
                      setFieldValue("cohortId", "");
                    }}
                    options={studies}
                    getOptionLabel={(option) =>
                      typeof option === "string" ? option : option.name
                    }
                    isOptionEqualToValue={(option, value) =>
                      option.id === value.id
                    }
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        placeholder="Search Study (min 3 characters)"
                        error={
                          touched?.studyId && errors?.studyId ? true : false
                        }
                        helperText={
                          touched?.studyId && errors?.studyId
                            ? (errors?.studyId as string)
                            : " "
                        }
                      />
                    )}
                    loading={studiesLoading}
                    loadingText={<CircularProgress size={20} />}
                    noOptionsText="No Results"
                    clearOnBlur={true}
                  />
                </FormControl>

                <FormControl sx={InputWrapper}>
                  <FormLabel sx={LabelStyle}>
                    Cohorts <span style={{ color: "red" }}>*</span>
                  </FormLabel>
                  <Select
                    id="cohortId"
                    value={values.cohortId}
                    sx={{ mb: 2 }}
                    onChange={(e) => {
                      const id = e.target.value || "";
                      setFieldValue("cohortId", id);
                      if (id) {
                        handleParticipantSearch(values.studyId, id);
                      }
                      setParticipants([]);
                      setFieldValue("participantIds", []);
                    }}
                  >
                    {cohorts?.length ? (
                      cohorts?.map((item: any) => (
                        <MenuItem key={item.id} value={item.id}>
                          {item.name}
                        </MenuItem>
                      ))
                    ) : (
                      <MenuItem key={"None"} value={""} disabled>
                        {cohortLoading
                          ? "Loading"
                          : values.studyId
                          ? "No Data available"
                          : "Please select a study"}
                      </MenuItem>
                    )}
                  </Select>
                </FormControl>

                {/* Participant Selection (Multiple) */}
                <FormControl sx={InputWrapper}>
                  <FormLabel sx={LabelStyle}>
                    Participants <span style={{ color: "red" }}>*</span>
                  </FormLabel>
                  <Autocomplete
                    multiple
                    key={values.cohortId || "unselected"}
                    // filterOptions={(x) => x}
                    onChange={(_1: any, newValue: any) => {
                      const participantIds = newValue.map((p: any) => p.id);
                      setFieldValue("participantIds", participantIds);
                    }}
                    options={participants}
                    getOptionLabel={(option) =>
                      typeof option === "string" ? option : `${option.name}`
                    }
                    isOptionEqualToValue={(option, value) =>
                      option.id === value.id
                    }
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        placeholder="Search Participants"
                        error={
                          touched?.participantIds && errors?.participantIds
                            ? true
                            : false
                        }
                        helperText={
                          touched?.participantIds && errors?.participantIds
                            ? (errors?.participantIds as string)
                            : " "
                        }
                      />
                    )}
                    loading={participantsLoading}
                    loadingText={<CircularProgress size={20} />}
                    noOptionsText={
                      values.cohortId
                        ? "No Results"
                        : "Please select Cohort first"
                    }
                    clearOnBlur={true}
                    renderOption={(props, option) => (
                      <li {...props} key={option.id}>
                        {option.name}
                      </li>
                    )}
                    sx={{
                      "& .MuiInputBase-root": {
                        height: "auto",
                        minHeight: "54px",
                      },
                    }}
                  />
                </FormControl>

                {/* Inviter Selection (Multiple) */}
                <FormControl sx={InputWrapper}>
                  <FormLabel sx={LabelStyle}>
                    Internal Team <span style={{ color: "red" }}>*</span>
                  </FormLabel>
                  <Autocomplete
                    multiple
                    // filterOptions={(x) => x}
                    key={values.studyId || "unselected"}
                    onChange={(_1: any, newValue: any) => {
                      const inviterIds = newValue.map((i: any) => i.id);
                      setFieldValue("inviterIds", inviterIds);
                    }}
                    options={inviters}
                    getOptionLabel={(option) =>
                      typeof option === "string" ? option : option.name
                    }
                    isOptionEqualToValue={(option, value) =>
                      option.id === value.id
                    }
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        placeholder="Search Inviters"
                        helperText=" "
                      />
                    )}
                    loading={invitersLoading}
                    loadingText={<CircularProgress size={20} />}
                    noOptionsText={
                      values.studyId
                        ? "No Results"
                        : "Please select Study first"
                    }
                    clearOnBlur={true}
                    renderOption={(props, option) => (
                      <li {...props} key={option.id}>
                        {option.name}
                      </li>
                    )}
                    sx={{
                      "& .MuiInputBase-root": {
                        height: "auto",
                        minHeight: "54px",
                      },
                    }}
                  />
                </FormControl>

                {/* Notes */}
                <FormControl sx={InputWrapper}>
                  <FormLabel sx={LabelStyle}>Notes (Optional)</FormLabel>
                  <TextField
                    multiline
                    rows={4}
                    placeholder="Add any notes for this appointment..."
                    {...getFieldProps("notes")}
                    helperText=" "
                  />
                </FormControl>
              </Box>

              <Box>
                <Divider sx={{ mb: 2 }} />
                <Box sx={ModalDrawerButtonStyles}>
                  <Button variant="outlined" onClick={closeModal}>
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    variant="contained"
                    disabled={submitLoader}
                  >
                    {submitLoader ? (
                      <CircularProgress size={24} color="inherit" />
                    ) : (
                      "Create Appointment"
                    )}
                  </Button>
                </Box>
              </Box>
            </Form>
          )}
        </Formik>
      </Box>
    </Modal>
  );
};

export default AddAppointmentModal;
